import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import Modal, { ModalCloseButton } from './ui/Modal';

const ContactsSection = ({ patient, API_BASE_URL }) => {
  const [isEmergencyContactModalOpen, setIsEmergencyContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emergencyContactForm, setEmergencyContactForm] = useState({
    name: '',
    relation: '',
    phone: '',
    email: '',
    address: '',
    is_primary: false,
    notes: '',
  });

  const fetchEmergencyContacts = useCallback(async () => {
    if (!patient?.MRN) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/emergency-contacts/patient/${patient.MRN}`);
      if (response.ok) {
        const data = await response.json();
        setEmergencyContacts(data);
      }
    } catch (err) {
      console.error('Error fetching emergency contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [patient?.MRN, API_BASE_URL]);

  // Fetch emergency contacts
  useEffect(() => {
    if (patient?.MRN) {
      fetchEmergencyContacts();
    }
  }, [patient?.MRN, fetchEmergencyContacts]);

  const handleEmergencyContactSubmit = async () => {
    if (!patient?.MRN) {
      console.error('No MRN found for patient');
      return;
    }

    try {
      const url = editingContact
        ? `${API_BASE_URL}/emergency-contacts/patient/${patient.MRN}/${editingContact.id}`
        : `${API_BASE_URL}/emergency-contacts/patient/${patient.MRN}`;
      
      const method = editingContact ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emergencyContactForm),
      });

      if (!response.ok) throw new Error('Failed to save emergency contact');
      
      await fetchEmergencyContacts();
      setIsEmergencyContactModalOpen(false);
      setEditingContact(null);
      setEmergencyContactForm({
        name: '',
        relation: '',
        phone: '',
        email: '',
        address: '',
        is_primary: false,
        notes: '',
      });
    } catch (err) {
      console.error('Error saving emergency contact:', err);
      alert('Failed to save emergency contact. Please try again.');
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setEmergencyContactForm({
      name: contact.name || '',
      relation: contact.relation || '',
      phone: contact.phone || '',
      email: contact.email || '',
      address: contact.address || '',
      is_primary: contact.is_primary || false,
      notes: contact.notes || '',
    });
    setIsEmergencyContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this emergency contact?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/emergency-contacts/patient/${patient.MRN}/${contactId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete emergency contact');
      
      await fetchEmergencyContacts();
    } catch (err) {
      console.error('Error deleting emergency contact:', err);
      alert('Failed to delete emergency contact. Please try again.');
    }
  };

  const handleAddNew = () => {
    setEditingContact(null);
    setEmergencyContactForm({
      name: '',
      relation: '',
      phone: '',
      email: '',
      address: '',
      is_primary: false,
      notes: '',
    });
    setIsEmergencyContactModalOpen(true);
  };

  // Prevent rendering before patient is loaded
  if (!patient) {
    return (
      <div className="border border-teal-300 rounded-lg p-4 bg-gray-50 text-gray-500 italic">
        Loading contact info...
      </div>
    );
  }

  return (
    <div className="border border-teal-300 rounded-lg p-4 space-y-4 bg-white shadow-sm">
        <div className="flex justify-between items-center border-b border-teal-300 pb-2">
          <h3 className="font-semibold text-teal-700 text-lg">Emergency Contacts</h3>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-1 text-teal-600 hover:text-teal-800 transition px-3 py-1 rounded border border-teal-300 hover:bg-teal-50"
            title="Add Emergency Contact"
          >
            <Plus size={18} />
            Add Contact
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading emergency contacts...</div>
        ) : emergencyContacts.length === 0 ? (
          <div className="text-center py-4 text-gray-500 italic">
            No emergency contacts found. Click "Add Contact" to add one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-teal-50 border-b border-teal-200">
                  <th className="text-left p-2 text-sm font-semibold text-teal-700">Name</th>
                  <th className="text-left p-2 text-sm font-semibold text-teal-700">Relation</th>
                  <th className="text-left p-2 text-sm font-semibold text-teal-700">Phone</th>
                  <th className="text-left p-2 text-sm font-semibold text-teal-700">Email</th>
                  <th className="text-left p-2 text-sm font-semibold text-teal-700">Primary</th>
                  <th className="text-left p-2 text-sm font-semibold text-teal-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {emergencyContacts.map((contact) => (
                  <tr key={contact.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-2 text-sm">{contact.name}</td>
                    <td className="p-2 text-sm">{contact.relation || '-'}</td>
                    <td className="p-2 text-sm">{contact.phone}</td>
                    <td className="p-2 text-sm">{contact.email || '-'}</td>
                    <td className="p-2 text-sm">
                      {contact.is_primary ? (
                        <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-semibold">
                          Primary
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="text-teal-600 hover:text-teal-800 transition"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-800 transition"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal for Emergency Contact Add/Edit */}
        <Modal
          open={isEmergencyContactModalOpen}
          onClose={() => {
            setIsEmergencyContactModalOpen(false);
            setEditingContact(null);
          }}
        >
            <div className="bg-white p-6 rounded-lg w-full max-w-md space-y-4 shadow-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center border-b pb-2">
                <h2 className="text-lg font-semibold text-teal-700">
                  {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
                </h2>
                <ModalCloseButton
                  onClose={() => {
                    setIsEmergencyContactModalOpen(false);
                    setEditingContact(null);
                  }}
                />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Contact Name"
                    className="w-full border p-2 rounded"
                    value={emergencyContactForm.name}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                  <input
                    type="text"
                    placeholder="e.g., Spouse, Parent, Sibling"
                    className="w-full border p-2 rounded"
                    value={emergencyContactForm.relation}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, relation: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Phone Number"
                    className="w-full border p-2 rounded"
                    value={emergencyContactForm.phone}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full border p-2 rounded"
                    value={emergencyContactForm.email}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    placeholder="Address"
                    className="w-full border p-2 rounded"
                    value={emergencyContactForm.address}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, address: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_primary"
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    checked={emergencyContactForm.is_primary}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, is_primary: e.target.checked })}
                  />
                  <label htmlFor="is_primary" className="text-sm font-medium text-gray-700">
                    Set as Primary Contact
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    placeholder="Additional notes (optional)"
                    className="w-full border p-2 rounded"
                    rows="3"
                    value={emergencyContactForm.notes}
                    onChange={(e) => setEmergencyContactForm({ ...emergencyContactForm, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  onClick={() => {
                    setIsEmergencyContactModalOpen(false);
                    setEditingContact(null);
                  }}
                  className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmergencyContactSubmit}
                  disabled={!emergencyContactForm.name || !emergencyContactForm.phone}
                  className="px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {editingContact ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
        </Modal>
    </div>
  );
};

export default ContactsSection;
